import Clear from '@mui/icons-material/Clear'
import Options from '@mui/icons-material/MoreVert'
import { Drawer, Grid, IconButton, InputAdornment } from '@mui/material'
import { UnparsedClient } from 'models/client'
import { IInvoice } from 'models/invoices.models'
import {
  getFormattedCurrency,
  getFormattedDate,
} from 'lib/helpers/shared.helpers'
import {
  getFormattedEmisor,
  getFormattedReceptor,
  getFormattedSerieFolio,
} from 'lib/utils/Invoices/invoices.utils'
import { useCFDIsContainerContext } from 'containers/Invoices/CFDIs/CFDIsContainer/CFDIsContainer'
import IOCustomForm from 'components/FormComponents/IOComponents/IOCustomForm/IOCustomForm'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import CFDIArticulo69B from 'components/Invoices/CFDIArticulo69B/CFDIArticulo69B'
import CFDIMovement from 'components/Invoices/CFDIMovement/CFDIMovement'
import CFDIPaymentStatus from 'components/Invoices/CFDIPaymentStatus/CFDIPaymentStatus'
import CFDIStatus from 'components/Invoices/CFDIStatus/CFDIStatus'
import './styles.scss'

const CFDIInfo = () => {
  const { activeInvoice, openInfo, updateState } = useCFDIsContainerContext()

  const typedInvoice = activeInvoice as IInvoice

  return (
    <Drawer
      id="cfdiInfoDrawer"
      className="cfdi-info-component"
      anchor="right"
      open={openInfo}
      SlideProps={{ mountOnEnter: true, unmountOnExit: true }}>
      <IOCustomForm onSubmit={() => true} onValidity={() => true}>
        <Grid container className="invoice-information-container">
          <Grid item xs={12} className="actions-wrapper">
            <IconButton
              id="closeDrawerBtn"
              size="small"
              color="primary"
              onClick={() => {
                updateState(draft => {
                  draft.openInfo = false
                  draft.seeMoreAnchor = null
                })
              }}>
              <Clear />
            </IconButton>
            <IconButton
              id="drawerOptionsBtn"
              size="small"
              color="primary"
              onClick={e => {
                updateState(draft => {
                  draft.seeMoreAnchor = e.currentTarget
                })
              }}>
              <Options />
            </IconButton>
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="createdAt"
              label="Fecha"
              value={getFormattedDate(typedInvoice?.createdAt)}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="serie"
              label="Serie/Folio"
              value={getFormattedSerieFolio(typedInvoice?.serie, activeInvoice)}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="uuid"
              label="UUID"
              value={
                typedInvoice?.uuid?.toUpperCase() ??
                'XXXXXXXX-XXXX-XXXX-XXXXXXXXXXXX'
              }
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="receptor"
              label="Receptor"
              value={getFormattedReceptor(typedInvoice?.clients)}
              addon={
                <small>
                  <i>{typedInvoice?.clients?.rfc ?? '---'}</i>
                </small>
              }
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="emisor"
              label="RFC"
              value={getFormattedEmisor(typedInvoice?.emisor)}
              addon={
                <small>
                  <i>{typedInvoice?.emisor?.rfc ?? '---'}</i>
                </small>
              }
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="currency"
              label="Moneda"
              value={
                typedInvoice?.currency
                  ? `${typedInvoice.currency.value} - ${typedInvoice.currency.label}`
                  : '---'
              }
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="subtotal"
              label="Subtotal"
              value={getFormattedCurrency(typedInvoice?.subtotal)}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="taxes"
              label="Impuestos"
              value={getFormattedCurrency(typedInvoice?.taxes)}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="total"
              label="Total"
              value={getFormattedCurrency(typedInvoice?.total)}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="type"
              label="Tipo"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment className="adornment" position="start">
                    <CFDIMovement
                      type={typedInvoice?.movementType ?? ''}
                      addTooltip={false}
                      returnLabel
                    />
                  </InputAdornment>
                ),
              }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="status"
              label="Estatus factura"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment className="adornment" position="start">
                    <CFDIStatus
                      status={typedInvoice?.status ?? 0}
                      row={activeInvoice}
                      addTooltip={false}
                      returnLabel
                    />
                  </InputAdornment>
                ),
              }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="articulo69bStatus"
              label="Art. 69B"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment className="adornment" position="start">
                    <CFDIArticulo69B
                      receptor={typedInvoice?.clients ?? ({} as UnparsedClient)}
                      addTooltip={false}
                      returnLabel
                    />
                  </InputAdornment>
                ),
              }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="statusPayment"
              label="Estatus pago"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment className="adornment" position="start">
                    <CFDIPaymentStatus
                      status={typedInvoice?.statusPayment ?? 0}
                      row={activeInvoice}
                      addTooltip={false}
                      returnLabel
                    />
                  </InputAdornment>
                ),
              }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="sent"
              label="Enviado"
              value={getFormattedDate(typedInvoice?.sent)}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              exclude
            />
          </Grid>
        </Grid>
      </IOCustomForm>
    </Drawer>
  )
}

export default CFDIInfo
